import { Injectable } from '@angular/core';
import { Howl } from 'howler';
import { BehaviorSubject, interval, Subscription } from 'rxjs';
import { MediaSession } from '@jofr/capacitor-media-session';
import { Logger } from '@app/core/logger.service';
import { Router, UrlSegment } from '@angular/router';
const log = new Logger('AudioService');
export interface Track {
  id: string;
  path: string;
  thumbnail?: string;
  title?: string;
  dateStr?: string;
  description?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AudioWebService {
  private sound: Howl | undefined;
  private progressInterval?: Subscription;
  private _currentTime = new BehaviorSubject<number>(0);
  private _duration = new BehaviorSubject<number>(0);
  private _onEnded = new BehaviorSubject<any>(undefined);
  private track!: Track;
  public currentTime$ = this._currentTime.asObservable();
  public duration$ = this._duration.asObservable();
  public onEnded$ = this._onEnded.asObservable();
  public isLoading: boolean = false;
  private items: Track[] = [];

  constructor(
    private router: Router
  ) { }

  setTrack(track: Track, play: boolean = false) {
    this.isLoading = true;
    if (this.sound) {
      this.stop();
    }
    this.Track = track;
    if (play === true) {
      try {
        this.sound = new Howl({
          src: [track.path],
          html5: true,
          onload: async () => {
            this.isLoading = false;
            const duration: number = this.sound.duration();
            this.initMediaSession(track, duration);
          },
          onloaderror: () => {
            this.isLoading = false;
          },
          onplayerror: () => {
            this.isLoading = false;
          },
          onplay: () => {
            const savedProgress = this.getSavedProgress(track.id);
            this._duration.next(this.sound!.duration());
            if (savedProgress > 0) {
              this.sound!.seek(savedProgress);
              this._currentTime.next(savedProgress);
            }
            this.trackProgress();
            MediaSession.setPlaybackState({ playbackState: 'playing' });
            this.isLoading = false;
          },
          onpause: () => {
            MediaSession.setPlaybackState({ playbackState: 'paused' });
          },
          onend: () => {
            this.stopTracking();
            this.clearProgress();
            this.isLoading = false;
            this._onEnded.next(this.track);
          }
        });
        this.sound.play();
      } catch (error) {
        log.error("Error al reproducir el audio:", error);
        this.isLoading = false;
      }
    }
  }

  async togglePlayPause() {
    try {
      if (this.sound && this.sound.playing()) {
        this.sound.pause();
        this.saveProgress();
        this.stopTracking();
      } else {
        this.sound?.play();
        this.trackProgress();
      }
    } catch (error) {
      log.error(error);
    }
  }

  stop() {
    this.sound.unload();
    this.sound?.stop();
    this.saveProgress();
    this.stopTracking();
    this._currentTime.next(0);
  }

  seek(seek: any): void {
    if (this.sound) {
      const value: number = Number(seek.detail.value);
      this.sound.seek(value);
      this._currentTime.next(value);
      this.saveProgress();
    }
  }

  setRate(rate: number) {
    if (this.sound) {
      this.sound.rate(rate);
    }
  }

  private trackProgress() {
    this.stopTracking();
    this.progressInterval = interval(1000).subscribe(() => {
      if (this.sound?.playing()) {
        const current = this.sound.seek() as number;
        this._currentTime.next(current);
        this.saveProgress();
      }
    });
  }

  private stopTracking() {
    this.progressInterval?.unsubscribe();
  }

  private saveProgress() {
    const position = this.sound?.seek() as number ?? 0;
    const duration = this.sound?.duration() ?? 0;
    localStorage.setItem(
      `progress_${this.track.id}`,
      JSON.stringify({ position, duration, updated: Date.now() })
    );
  }

  private getSavedProgress(trackId: string): number {
    const data = localStorage.getItem(`progress_${trackId}`);
    if (data) {
      try {
        const parsed = JSON.parse(data);
        return parsed.position ?? 0;
      } catch {
        return 0;
      }
    }
    return 0;
  }

  private clearProgress() {
    if (this.track?.id) {
      localStorage.removeItem(`progress_${this.track.id}`);
    }
  }

  private async initMediaSession(track: Track, duration: number) {
    try {
      await MediaSession.setMetadata({
        title: String(track?.title) || '',
        artist: 'Conectimed ',
        album: 'Podcast',
        artwork: [
          { src: track?.thumbnail || './assets/imgs/logo.png', type: 'image/png', sizes: '512x512' }
        ]
      });
      MediaSession.setPositionState({
        duration: duration,
        position: 0
      })
      MediaSession.setPlaybackState({ playbackState: 'playing' });
    } catch (e) {
      log.error("****** enacment:error ******", e);
    }

    MediaSession.setActionHandler({ action: 'play' }, async () => {
      this.togglePlayPause();
    });

    MediaSession.setActionHandler({ action: 'pause' }, async () => {
      this.togglePlayPause();
    });

    MediaSession.setActionHandler({ action: 'nexttrack' }, () => {
      this.nextTrack();
    });

    MediaSession.setActionHandler({ action: 'previoustrack' }, () => {
      this.prevTrack();
    });
  }

  get isPlaying(): boolean {
    try {
      if (this.sound) {
        return this.sound && this.sound.playing() && this.sound.playing() === true ? true : false;
      } else {
        return false;
      }
    } catch (e) {
      return false;
    }
  }

  get getCurrentSound(): Howl | undefined {
    return this.sound;
  }

  get Track(): Track {
    return this.track;
  }

  set Track(track: Track) {
    this.track = track;
  }

  set playList(playlist: Track[]) {
    this.items = playlist;
  }

  async prevTrack() {
    if (this.Track && this.Track.id) {
      try {
        const index: number = this.items.map(e => e.id).indexOf(this.Track.id);
        const currentTime: number = this.sound.seek();
        if (currentTime > 3 || index === 0) {
          this.setTrack(this.Track, true);
        } else {
          if (index > -1) {
            const item = this.items[index - 1];
            if (item) {
              this.setTrack(item, true);
              this.navigate(item.id);
            }
          }
        }
      } catch (error) {
        log.error(error);
      }
    }
  }

  async nextTrack() {
    if (this.Track && this.Track.id) {
      const index: number = this.items.map(e => e.id).indexOf(this.Track.id);
      if (index > -1) {
        const item = this.items[index + 1];
        if (item) {
          this.setTrack(item, true);
          this.navigate(item.id);
        }
      }
    }
  }

  private navigate(nuevoId: string) {
    const currentUrl = this.router.url;
    const urlTree = this.router.parseUrl(currentUrl);
    const segments = urlTree.root.children['primary'].segments;
    if (
      segments.length === 3 &&
      segments[0].path === 'podcast' &&
      segments[1].path === 'detail'
    ) {
      segments[2] = new UrlSegment(nuevoId, {});
      const newRoute = '/' + segments.map(s => s.path).join('/');
      const query = urlTree.queryParams;
      const queryString = new URLSearchParams(query).toString();
      const finalUrl = queryString ? `${newRoute}?${queryString}` : newRoute;
      window.history.replaceState({}, '', finalUrl);
    } else {
      log.warn('Routing Error');
    }
  }
}
